import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { Save, Mail, Phone, MapPin, Clock, Globe, Loader2 } from 'lucide-react';
import api from '../../services/api';

const AdminContactSettings = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    email: '',
    phone: '',
    location: '',
    business_hours: {
      weekdays: '',
      saturday: '',
      sunday: ''
    },
    social_links: {
      github: '',
      linkedin: '',
      twitter: '',
      instagram: ''
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/contact');
      setSettings(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load contact settings',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/settings/contact', settings);
      toast({
        title: 'Success',
        description: 'Contact settings updated successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateBusinessHours = (field, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: { ...prev.business_hours, [field]: value }
    }));
  };

  const updateSocialLink = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-light text-white mb-2">Contact Information</h1>
          <p className="text-gray-400">Manage your public contact details</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-white text-black hover:bg-gray-200"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Contact Info */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-6">
          <h2 className="text-xl font-light text-white flex items-center">
            <Mail className="w-5 h-5 mr-3 text-gray-400" />
            Basic Information
          </h2>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Email Address
            </label>
            <Input
              type="email"
              value={settings.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="bg-transparent border-white/20 focus:border-white text-white"
              placeholder="hello@example.com"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="tel"
                value={settings.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white pl-10"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="text"
                value={settings.location}
                onChange={(e) => updateField('location', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white pl-10"
                placeholder="San Francisco, CA"
              />
            </div>
          </div>
        </div>

        {/* Business Hours */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-6">
          <h2 className="text-xl font-light text-white flex items-center">
            <Clock className="w-5 h-5 mr-3 text-gray-400" />
            Business Hours
          </h2>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Weekdays
            </label>
            <Input
              type="text"
              value={settings.business_hours?.weekdays || ''}
              onChange={(e) => updateBusinessHours('weekdays', e.target.value)}
              className="bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Monday - Friday: 9:00 AM - 6:00 PM"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Saturday
            </label>
            <Input
              type="text"
              value={settings.business_hours?.saturday || ''}
              onChange={(e) => updateBusinessHours('saturday', e.target.value)}
              className="bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Saturday: 10:00 AM - 4:00 PM"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Sunday
            </label>
            <Input
              type="text"
              value={settings.business_hours?.sunday || ''}
              onChange={(e) => updateBusinessHours('sunday', e.target.value)}
              className="bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Sunday: Closed"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-6 lg:col-span-2">
          <h2 className="text-xl font-light text-white flex items-center">
            <Globe className="w-5 h-5 mr-3 text-gray-400" />
            Social Links
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                GitHub
              </label>
              <Input
                type="url"
                value={settings.social_links?.github || ''}
                onChange={(e) => updateSocialLink('github', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white"
                placeholder="https://github.com/username"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                LinkedIn
              </label>
              <Input
                type="url"
                value={settings.social_links?.linkedin || ''}
                onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                Twitter / X
              </label>
              <Input
                type="url"
                value={settings.social_links?.twitter || ''}
                onChange={(e) => updateSocialLink('twitter', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white"
                placeholder="https://twitter.com/username"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                Instagram
              </label>
              <Input
                type="url"
                value={settings.social_links?.instagram || ''}
                onChange={(e) => updateSocialLink('instagram', e.target.value)}
                className="bg-transparent border-white/20 focus:border-white text-white"
                placeholder="https://instagram.com/username"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContactSettings;
